/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Foundational style overrides
import styles from '../theme/foundations/styles'
import colors from './foundations/colors'
import gradients from '@salesforce/retail-react-app/app/theme/foundations/gradients'
import sizes from './foundations/sizes'
import space from '@salesforce/retail-react-app/app/theme/foundations/space'
import layerStyles from './foundations/layerStyles'
import shadows from '@salesforce/retail-react-app/app/theme/foundations/shadows'
import fonts from './foundations/fonts'

// Base component style overrides
import Alert from './components/base/alert'
import Accordion from './components/base/accordion'
import Badge from '@salesforce/retail-react-app/app/theme/components/base/badge'
import Button from './components/base/button'
import Checkbox from '../theme/components/base/checkbox'
import Container from '@salesforce/retail-react-app/app/theme/components/base/container'
import Drawer from './components/base/drawer'
import FormLabel from './components/base/formLabel'
import Form from './components/base/form'
import Icon from '@salesforce/retail-react-app/app/theme/components/base/icon'
import Input from './components/base/input'
import Modal from '@salesforce/retail-react-app/app/theme/components/base/modal'
import Radio from './components/base/radio'
import Select from './components/base/select'
import Skeleton from '@salesforce/retail-react-app/app/theme/components/base/skeleton'
import Tooltip from '@salesforce/retail-react-app/app/theme/components/base/tooltip'
import Popover from './components/base/popover'
import Text from './components/base/text'
import Swiper from './components/base/swiper'
import Heading from './components/base/heading'
import CustomToast from './components/base/customToast'

// Project Component style overrides
import App from '@salesforce/retail-react-app/app/theme/components/project/_app'
import Breadcrumb from './components/project/breadcrumb'
import Header from '@salesforce/retail-react-app/app/theme/components/project/header'
import ListMenu from './components/project/list-menu'
import Footer from './components/project/footer'
import CheckoutFooter from '@salesforce/retail-react-app/app/theme/components/project/checkout-footer'
import LinksList from './components/project/links-list'
import DrawerMenu from './components/project/drawer-menu'
import NestedAccordion from '@salesforce/retail-react-app/app/theme/components/project/nested-accordion'
import LocaleSelector from '@salesforce/retail-react-app/app/theme/components/project/locale-selector'
import OfflineBanner from '@salesforce/retail-react-app/app/theme/components/project/offline-banner'
import Pagination from '@salesforce/retail-react-app/app/theme/components/project/pagination'
import ProductTile from './components/project/product-tile'
import SocialIcons from '@salesforce/retail-react-app/app/theme/components/project/social-icons'
import SwatchGroup from './components/project/swatch-group'
import ImageGallery from '@salesforce/retail-react-app/app/theme/components/project/image-gallery'
import Cart from './components/project/cart'
import Loyalty from './components/base/loyalty'
import CustomAdyen from './components/base/adyen'
import CustomAccountAdyenPayments from './components/base/accountAdyenPayments'
import DeliveryOptions from './components/project/delivery-options'
import ProductVideoPlayer from './components/project/product-video-player'
import ImageGalleryVideo from './components/project/image-gallery-video'

import StorePickup from './components/project/store-pickup'
import WriteReview from './components/project/write-review'
import Newsletter from './components/project/newsletter'
import FooterNavigationLinks from './components/project/footer-navigation-links'
import OrderHistory from './components/project/order-history'
import OrderDetailsDSS from './components/project/order-details-dss'
import AddressBook from './components/project/address-book'
import MyRewards from './components/project/my-rewards'
import StoreLocator from './components/project/store-locator'
import StoreInfo from './components/project/store-info'
import OrderDetail from './components/project/order-detail'
import DssOrderActionError from './components/project/dss-order-action-error'

/* START: CMS Styling */

/* Global */
import GlobalCMS from '../components/optimizely/styles/index'

/* Layouts */
import HeroPanelBlock from '../components/optimizely/layouts/heroPanelBlock/styles/index'
import WidgetGridBlock from '../components/optimizely/layouts/widgetGridBlock/styles/index'
import CategoryBannerBlock from '../components/optimizely/layouts/categoryBannerBlock/styles/index'
import FullPanelBlock from '../components/optimizely/layouts/fullPanelBlock/styles/index'
import WebFooterTopBarBlock from '../components/optimizely/layouts/webFooterTopBarBlock/styles/index'
import TitleDescriptionXHtmlBlock from '../components/optimizely/layouts/TitleDescriptionXHtmlBlock/styles/index'
import CtaItemListBlock from '../components/optimizely/layouts/CtaItemListBlock/styles/index'
import CategoryCarouselBlock from '../components/optimizely/layouts/categoryCarouselBlock/styles/index'
import CategoryCarouselListBlock from '../components/optimizely/layouts/categoryCarouselListBlock/styles/index'
import ImageTitleDescriptionBlock from '../components/optimizely/layouts/imageTitleDescriptionBlock/styles/index'
import BigImageAndProductListBlock from '../components/optimizely/layouts/bigImageAndProductListBlock/styles/index'
import ErrorPageBlock from '../components/optimizely/layouts/errorPageBlock/styles/index'
import PanelCtaBlock from '../components/optimizely/layouts/panelCtaBlock/styles/index'
import SeoFooterStyleBlock from '../components/optimizely/layouts/seoFooterStyleBlock/styles/index'
import ContentWrapperBlock from '../components/optimizely/layouts/contentWrapperBlock/styles/index'
import LeftRightContentBlock from '../components/optimizely/layouts/LeftRightContentBlock/styles/index'

/* Assets */
import VideoFile from '../components/optimizely/assets/videoFile/styles/index'
import CtaItemBlock from '../components/optimizely/assets/ctaItemBlock/styles/index'
import WidgetTileBlock from '../components/optimizely/assets/widgetTileBlock/styles/index'
import ImageFile from '../components/optimizely/assets/imageFile/styles/index'
import WebFooterTopBarColumnBlock from '../components/optimizely/assets/webFooterTopBarColumnBlock/styles/index'
import CategoryLinkBlock from '../components/optimizely/assets/categoryLinkBlock/styles/index'
import ProductLinkBlock from '../components/optimizely/assets/productLinkBlock/styles/index'
import MapViewBlock from '../components/optimizely/assets/mapViewBlock/styles/index'
import ResponsiveTextBlock from '../components/optimizely/assets/responsiveTextBlock/styles/index'
import RewardTileBlock from '../components/optimizely/assets/rewardTileBlock/styles/index'
import TileModalBlock from '../components/optimizely/assets/tileModalBlock/styles/index'
import MegaMenuIlustrationBlock from '../components/optimizely/assets/megaMenuIlustrationBlock/styles/index'

/* Common without typename or used directly on other components */
import CtaBlock from '../components/optimizely/common/ctaBlock/styles/index'
import GridInteruptorBlock from '../components/optimizely/common/gridInteruptorBlock/styles/index'

/* END: CMS Styling */

// Please refer to the Chakra-Ui theme customization docs found
// here https://chakra-ui.com/docs/theming/customize-theme to learn
// more about extending and overriding themes for your project.
export const overrides = {
    styles,
    layerStyles,
    colors,
    sizes,
    space,
    gradients,
    shadows,
    fonts,
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        Container,
        Drawer,
        FormLabel,
        Form,
        Icon,
        Input,
        Modal,
        Popover,
        Radio,
        Select,
        Skeleton,
        Tooltip,
        Text,
        Swiper,
        Heading,
        CustomToast,

        // project components
        App,
        Breadcrumb,
        Header,
        Footer,
        CheckoutFooter,
        LinksList,
        ListMenu,
        DrawerMenu,
        NestedAccordion,
        LocaleSelector,
        OfflineBanner,
        SocialIcons,
        Pagination,
        ProductTile,
        ProductVideoPlayer,
        ImageGalleryVideo,
        SwatchGroup,
        ImageGallery,
        Loyalty,
        Cart,
        StorePickup,
        WriteReview,
        CustomAdyen,
        Newsletter,
        FooterNavigationLinks,
        AddressBook,
        CustomAccountAdyenPayments,
        MyRewards,
        StoreLocator,
        StoreInfo,
        DeliveryOptions,
        OrderHistory,
        OrderDetailsDSS,
        DssOrderActionError,
        OrderDetail,
        /* START - CMS Styling*/

        GlobalCMS,

        // Layouts
        HeroPanelBlock,
        WidgetGridBlock,
        CategoryBannerBlock,
        FullPanelBlock,
        WebFooterTopBarBlock,
        TitleDescriptionXHtmlBlock,
        CtaItemListBlock,
        CategoryCarouselBlock,
        CategoryCarouselListBlock,
        ImageTitleDescriptionBlock,
        BigImageAndProductListBlock,
        ErrorPageBlock,
        PanelCtaBlock,
        ContentWrapperBlock,
        LeftRightContentBlock,

        // Assets
        VideoFile,
        CtaItemBlock,
        WidgetTileBlock,
        ImageFile,
        WebFooterTopBarColumnBlock,
        CategoryLinkBlock,
        ProductLinkBlock,
        MapViewBlock,
        ResponsiveTextBlock,
        RewardTileBlock,
        TileModalBlock,
        MegaMenuIlustrationBlock,
        SeoFooterStyleBlock,

        /* Common */
        CtaBlock,
        GridInteruptorBlock

        /* END - CMS Styling*/
    }
}

export default extendTheme(overrides)